<div class="settings-page">

  <aside class="settings-nav">
    <h1 class="settings-title">{{ 'settings.title' | t }}</h1>
    <nav>
      @for (item of sections; track item.id) {
        <a class="settings-nav-link" [routerLink]="['/settings', item.id]" [class.active]="item.id === section">
          <mat-icon>{{item.icon}}</mat-icon>
          <span>{{ item.labelKey | t }}</span>
        </a>
      }
    </nav>
  </aside>

  <section class="settings-content">
    <button type="button" class="link-back" (click)="goBack()">
      <mat-icon>arrow_back</mat-icon> {{ 'common.back' | t }}
    </button>

    <!-- La langue est un réglage de l'appareil : modifiable même sans être connecté -->
    <ng-template #languageRow>
      <div class="setting-row">
        <div class="setting-label">
          {{ 'settings.preferences.language' | t }}
        </div>
        <div class="setting-value">
          <select class="select-input" [ngModel]="languageChoice" (ngModelChange)="setLanguage($event)"
            [attr.aria-label]="'settings.preferences.language' | t">
            <option value="">{{ 'settings.preferences.deviceLanguage' | t: { language: deviceLanguageName } }}</option>
            @for (language of i18n.languages; track language.code) {
              <option [value]="language.code" [attr.lang]="language.code" [attr.dir]="language.dir">{{language.nativeName}}</option>
            }
          </select>
          <small class="muted">{{ 'settings.preferences.languageHint' | t }}</small>
        </div>
      </div>
      <div class="setting-row">
        <div class="setting-label">
          {{ 'settings.preferences.theme' | t }}
        </div>
        <div class="setting-value">
          <select class="select-input" [ngModel]="themeChoice" (ngModelChange)="setTheme($event)"
            [attr.aria-label]="'settings.preferences.theme' | t">
            <option value="">{{ 'settings.preferences.themeAuto' | t }}</option>
            <option value="light">{{ 'settings.preferences.themeLight' | t }}</option>
            <option value="dark">{{ 'settings.preferences.themeDark' | t }}</option>
          </select>
          <small class="muted">{{ 'settings.preferences.themeHint' | t }}</small>
        </div>
      </div>
    </ng-template>

    @if (!isLoggedIn) {
      @if (section === 'preferences') {
        <h2 class="section-title">{{ 'settings.sections.preferences' | t }}</h2>
        <ng-container [ngTemplateOutlet]="languageRow"></ng-container>
        <hr class="separator" />
      } @else {
        <h2 class="section-title">{{ 'settings.title' | t }}</h2>
      }
      <p class="muted">{{ 'settings.loginPrompt' | t }}</p>
      <button type="button" class="btn-pill btn-pill-accent" (click)="openLogin()">{{ 'header.loginOrSignup' | t }}</button>
    } @else if (loading && !account) {
      <p class="muted">{{ 'settings.loading' | t }}</p>
    } @else if (loadError && !account) {
      <p class="error-text">{{loadError}}</p>
      <button type="button" class="btn-pill btn-pill-outline" (click)="loadAccount()">{{ 'settings.retry' | t }}</button>
    } @else if (account) {

      @switch (section) {

        <!-- ================= Profil ================= -->
        @case ('profil') {
          <h2 class="section-title">{{ 'settings.sections.profil' | t }}</h2>

          <div class="setting-row">
            <div class="setting-label">
              {{ 'settings.profile.avatar' | t }}
              <small>{{ 'common.optional' | t }}</small>
            </div>
            <div class="setting-value avatar-block">
              <img class="avatar-preview" [src]="avatarSrc" [alt]="'settings.profile.avatar' | t" />
              <div class="button-pair">
                <button type="button" class="btn-pill btn-pill-outline" [disabled]="saving" (click)="avatarInput.click()">{{ 'settings.profile.replace' | t }}</button>
                <button type="button" class="btn-text btn-text-danger" [disabled]="saving || !account.avatar" (click)="removeAvatar()">{{ 'settings.profile.clear' | t }}</button>
              </div>
              <small class="muted">{{ 'settings.profile.avatarHint' | t }}</small>
              <input #avatarInput type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden (change)="onAvatarSelected($event)" />
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.profile.pseudo' | t }}</div>
            <div class="setting-value">
              <span class="value-text">{{account.pseudo || '…'}}</span>
              @if (editing === 'pseudo') {
                <form class="inline-form" (ngSubmit)="savePseudo()">
                  <input class="text-input" name="pseudo" [(ngModel)]="pseudoInput" maxlength="30" autocomplete="nickname" />
                  @if (formError) { <span class="error-text">{{formError}}</span> }
                  <div class="button-pair">
                    <button type="submit" class="btn-pill btn-pill-accent" [disabled]="saving || !pseudoInput.trim()">{{ 'common.save' | t }}</button>
                    <button type="button" class="btn-pill btn-pill-outline" (click)="cancelEdit()">{{ 'common.cancel' | t }}</button>
                  </div>
                </form>
              } @else {
                <button type="button" class="btn-pill btn-pill-outline btn-block" [disabled]="!canChangePseudo" (click)="startEdit('pseudo')">{{ 'settings.profile.editPseudo' | t }}</button>
              }
              <small class="muted">
                {{ 'settings.profile.pseudoRule' | t }}
                @if (!canChangePseudo) { {{ 'settings.profile.nextPseudoChange' | t: { date: (account.nextPseudoChangeAt | date: 'dd/MM/yyyy') } }} }
              </small>
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.profile.description' | t }}</div>
            <div class="setting-value">
              <span class="value-text">{{account.description || '…'}}</span>
              @if (editing === 'description') {
                <form class="inline-form" (ngSubmit)="saveDescription()">
                  <textarea class="text-input" name="description" rows="3" maxlength="500" [(ngModel)]="descriptionInput"></textarea>
                  <small class="muted">{{descriptionInput.length}}/500</small>
                  @if (formError) { <span class="error-text">{{formError}}</span> }
                  <div class="button-pair">
                    <button type="submit" class="btn-pill btn-pill-accent" [disabled]="saving">{{ 'common.save' | t }}</button>
                    <button type="button" class="btn-pill btn-pill-outline" (click)="cancelEdit()">{{ 'common.cancel' | t }}</button>
                  </div>
                </form>
              } @else {
                <button type="button" class="btn-pill btn-pill-outline btn-block" (click)="startEdit('description')">{{ 'settings.profile.editDescription' | t }}</button>
              }
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.profile.email' | t }}</div>
            <div class="setting-value">
              <span class="value-text">{{account.email}}</span>
              @if (editing === 'email') {
                <form class="inline-form" (ngSubmit)="saveEmail()">
                  <input class="text-input" type="email" name="email" [placeholder]="'settings.profile.newEmail' | t" [(ngModel)]="emailInput" autocomplete="email" />
                  <input class="text-input" type="password" name="password" [placeholder]="'settings.profile.currentPassword' | t" [(ngModel)]="passwordInput" autocomplete="current-password" />
                  <small class="muted">{{ 'settings.profile.emailHint' | t }}</small>
                  @if (formError) { <span class="error-text">{{formError}}</span> }
                  <div class="button-pair">
                    <button type="submit" class="btn-pill btn-pill-accent" [disabled]="saving || !emailInput.trim() || !passwordInput">{{ 'common.save' | t }}</button>
                    <button type="button" class="btn-pill btn-pill-outline" (click)="cancelEdit()">{{ 'common.cancel' | t }}</button>
                  </div>
                </form>
              } @else {
                <button type="button" class="btn-pill btn-pill-outline btn-block" (click)="startEdit('email')">{{ 'settings.profile.editEmail' | t }}</button>
              }
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.profile.password' | t }}</div>
            <div class="setting-value">
              <span class="value-text password-dots">●●●●●●●●</span>
              @if (editing === 'password') {
                <form class="inline-form" (ngSubmit)="savePassword()">
                  <input class="text-input" type="password" name="current" [placeholder]="'settings.profile.currentPassword' | t" [(ngModel)]="passwordInput" autocomplete="current-password" />
                  <input class="text-input" type="password" name="new" [placeholder]="'settings.profile.newPassword' | t" [(ngModel)]="newPasswordInput" autocomplete="new-password" />
                  <input class="text-input" type="password" name="confirm" [placeholder]="'settings.profile.confirmNewPassword' | t" [(ngModel)]="confirmPasswordInput" autocomplete="new-password" />
                  <small class="muted">{{ 'settings.profile.passwordHint' | t }}</small>
                  @if (formError) { <span class="error-text">{{formError}}</span> }
                  <div class="button-pair">
                    <button type="submit" class="btn-pill btn-pill-accent" [disabled]="saving || !passwordInput || !newPasswordInput">{{ 'common.save' | t }}</button>
                    <button type="button" class="btn-pill btn-pill-outline" (click)="cancelEdit()">{{ 'common.cancel' | t }}</button>
                  </div>
                </form>
              } @else {
                <button type="button" class="btn-pill btn-pill-outline btn-block" (click)="startEdit('password')">{{ 'settings.profile.editPassword' | t }}</button>
              }
            </div>
          </div>

          <hr class="separator" />

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.profile.accountData' | t }}</div>
            <div class="setting-value">
              <button type="button" class="btn-pill btn-pill-outline btn-block" (click)="exportData()">{{ 'settings.profile.exportData' | t }}</button>
            </div>
          </div>

          <hr class="separator" />

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.profile.deleteAccount' | t }}</div>
            <div class="setting-value">
              @if (editing === 'delete') {
                <form class="inline-form" (ngSubmit)="deleteAccount()">
                  <small class="muted">{{ 'settings.profile.deleteHint' | t }}</small>
                  <input class="text-input" type="password" name="password" [placeholder]="'settings.profile.currentPassword' | t" [(ngModel)]="passwordInput" autocomplete="current-password" />
                  @if (formError) { <span class="error-text">{{formError}}</span> }
                  <div class="button-pair">
                    <button type="submit" class="btn-pill btn-pill-danger" [disabled]="saving || !passwordInput">{{ 'settings.profile.deleteForever' | t }}</button>
                    <button type="button" class="btn-pill btn-pill-outline" (click)="cancelEdit()">{{ 'common.cancel' | t }}</button>
                  </div>
                </form>
              } @else {
                <button type="button" class="btn-pill btn-pill-danger-outline btn-block" (click)="startEdit('delete')">{{ 'settings.profile.deleteAccount' | t }}</button>
              }
            </div>
          </div>
        }

        <!-- ================= Préférences ================= -->
        @case ('preferences') {
          <h2 class="section-title">{{ 'settings.sections.preferences' | t }}</h2>

          <ng-container [ngTemplateOutlet]="languageRow"></ng-container>

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.preferences.homePage' | t }}</div>
            <div class="setting-value">
              <select class="select-input" [ngModel]="preference('homePage')" (ngModelChange)="setPreference('homePage', $event)">
                @for (option of homePageOptions; track option.value) {
                  <option [value]="option.value">{{ option.labelKey | t }}</option>
                }
              </select>
            </div>
          </div>

          @for (group of preferenceGroups; track group.titleKey) {
            <div class="setting-row">
              <div class="setting-label">{{ group.titleKey | t }}</div>
              <div class="setting-value checkbox-list">
                @for (option of group.options; track option.key) {
                  <label class="checkbox-line">
                    <input type="checkbox" [checked]="preference(option.key)" (change)="setPreference(option.key, $any($event.target).checked)" />
                    <span>{{ option.labelKey | t }}</span>
                  </label>
                  @if (option.hintKey) { <small class="muted hint">{{ option.hintKey | t }}</small> }
                }
              </div>
            </div>
          }
        }

        <!-- ================= Réseaux sociaux ================= -->
        @case ('reseaux-sociaux') {
          <h2 class="section-title">{{ 'settings.sections.social' | t }}</h2>

          <div class="setting-row">
            <div class="setting-label">
              {{ 'settings.social.linkAccount' | t }}
              <small>{{ 'common.optional' | t }}</small>
            </div>
            <div class="setting-value">
              <button type="button" class="btn-pill btn-pill-outline btn-block social-btn" disabled>
                <span class="social-logo">G</span> {{ 'settings.social.google' | t }}
              </button>
              <button type="button" class="btn-pill btn-pill-outline btn-block social-btn" disabled>
                <mat-icon>phone_iphone</mat-icon> {{ 'settings.social.apple' | t }}
              </button>
              <small class="muted">{{ 'settings.social.unavailable' | t }}</small>
            </div>
          </div>
        }

        <!-- ================= Notifications ================= -->
        @case ('notifications') {
          <h2 class="section-title">{{ 'settings.sections.notifications' | t }}</h2>

          @for (group of notificationGroups; track group.titleKey; let last = $last) {
            <div class="setting-row">
              <div class="setting-label">{{ group.titleKey | t }}</div>
              <div class="setting-value checkbox-list">
                @for (option of group.options; track option.key) {
                  <label class="checkbox-line">
                    <input type="checkbox" [checked]="notification(option.key)" (change)="setNotification(option.key, $any($event.target).checked)" />
                    <span>{{ option.labelKey | t }}</span>
                  </label>
                }
              </div>
            </div>
            @if (!last) { <hr class="separator" /> }
          }
        }

        <!-- ================= Abonnements ================= -->
        @case ('abonnements') {
          <h2 class="section-title">{{ 'settings.sections.subscriptions' | t }}</h2>
          <p class="sub-title">{{ 'settings.subscriptions.manage' | t }}</p>

          @if (account.email) {
            <div class="subscription-row">
              <select class="select-input" [(ngModel)]="newsletterFrequency">
                @for (option of newsletterOptions; track option.value) {
                  <option [value]="option.value">{{ option.labelKey | t }}</option>
                }
              </select>
              @if (account.newsletter.subscribed && account.newsletter.frequency === newsletterFrequency) {
                <button type="button" class="btn-pill btn-pill-outline btn-wide" (click)="updateNewsletter(false)">{{ 'settings.subscriptions.unsubscribe' | t }}</button>
              } @else {
                <button type="button" class="btn-pill btn-pill-accent btn-wide" (click)="updateNewsletter(true)">
                  {{ (account.newsletter.subscribed ? 'settings.subscriptions.update' : 'settings.subscriptions.subscribe') | t }}
                </button>
              }
            </div>
            @if (account.newsletter.subscribed) {
              <small class="muted">
                {{ (account.newsletter.frequency === 'weekly' ? 'settings.subscriptions.subscribedWeekly' : 'settings.subscriptions.subscribedDaily') | t: { email: account.email } }}
              </small>
            }
            <hr class="separator" />
            <!-- Consentement distinct de la newsletter (RGPD) : décoché tant que l'utilisateur ne l'a pas activé -->
            <label class="checkbox-line partner-offers">
              <input type="checkbox" id="partnerOffers" [checked]="account.newsletter.partnerOffers" (change)="updatePartnerOffers($any($event.target).checked)" />
              <span>{{ 'consents.partnerOffers' | t }}</span>
            </label>
            @if (account.newsletter.pending) {
              <p class="muted">{{ 'settings.subscriptions.pending' | t }}</p>
            }
            <small class="muted">{{ 'consents.unsubscribeAnytime' | t }}</small>
          } @else {
            <p class="warning-text">
              {{ 'settings.subscriptions.noEmail' | t }}
              <a [routerLink]="['/settings', 'profil']">{{ 'settings.sections.profil' | t }}</a> {{ 'settings.subscriptions.noEmailEnd' | t }}
            </p>
          }
        }

        <!-- ================= Membres suivis / ignorés ================= -->
        @case ('membres') {
          <h2 class="section-title">{{ 'settings.sections.members' | t }}</h2>

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.members.following' | t }}</div>
            <div class="setting-value">
              @for (member of account.following; track member._id) {
                <div class="member-line">
                  <input type="checkbox" [checked]="selectedFollowing.has(member._id)"
                    (change)="toggleSelection(selectedFollowing, member._id, $any($event.target).checked)" [attr.aria-label]="'settings.members.select' | t: { pseudo: member.pseudo }" />
                  <img class="member-avatar" [src]="memberAvatar(member)" alt="" />
                  <span class="member-pseudo">{{member.pseudo}}</span>
                  <button type="button" class="icon-btn" (click)="unfollow([member._id])" [attr.aria-label]="'settings.members.unfollowMember' | t: { pseudo: member.pseudo }" [title]="'settings.members.unfollow' | t">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              } @empty {
                <p class="muted">{{ 'settings.members.noFollowing' | t }}</p>
              }
              <button type="button" class="btn-pill btn-pill-outline btn-block" [disabled]="selectedFollowing.size === 0"
                (click)="unfollow(selectedIds(selectedFollowing))">{{ 'settings.members.unfollowSelected' | t }}</button>
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-label">{{ 'settings.members.ignored' | t }}</div>
            <div class="setting-value">
              @for (member of account.ignored; track member._id) {
                <div class="member-line">
                  <input type="checkbox" [checked]="selectedIgnored.has(member._id)"
                    (change)="toggleSelection(selectedIgnored, member._id, $any($event.target).checked)" [attr.aria-label]="'settings.members.select' | t: { pseudo: member.pseudo }" />
                  <img class="member-avatar" [src]="memberAvatar(member)" alt="" />
                  <span class="member-pseudo">{{member.pseudo}}</span>
                  <button type="button" class="icon-btn" (click)="unignore([member._id])" [attr.aria-label]="'settings.members.unignoreMember' | t: { pseudo: member.pseudo }" [title]="'settings.members.unignore' | t">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              } @empty {
                <p class="muted">{{ 'settings.members.noIgnored' | t }}</p>
              }
              <button type="button" class="btn-pill btn-pill-outline btn-block" [disabled]="selectedIgnored.size === 0"
                (click)="unignore(selectedIds(selectedIgnored))">{{ 'settings.members.unignoreSelected' | t }}</button>
            </div>
          </div>
        }
      }
    }
  </section>
</div>
